import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@repo/ui'
import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { deleteProject } from '@/api/project/delete'

type DeleteProjectDialogProps = {
  open: boolean
  onClose: () => void
  projectId: number
  projectTitle: string
  orgSlug: string
  onDeleted?: () => void | Promise<void>
}

export function DeleteProjectDialog({
  open,
  onClose,
  projectId,
  projectTitle,
  orgSlug,
  onDeleted
}: DeleteProjectDialogProps) {
  const router = useRouter()
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleDelete() {
    setIsDeleting(true)
    setError(null)
    try {
      await deleteProject(projectId)
      await router.navigate({ to: '/$orgSlug', params: { orgSlug } })
      await onDeleted?.()
      await router.invalidate()
      onClose()
    } catch (err) {
      Sentry.captureException(err, { extra: { orgSlug, projectId, title: projectTitle } })
      setError(err instanceof Error ? err.message : 'Failed to delete workspace')
    } finally {
      setIsDeleting(false)
    }
  }

  function handleClose() {
    if (isDeleting) return
    setError(null)
    onClose()
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete &ldquo;{projectTitle}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <span className="block">
              This will permanently delete the workspace and all of its content, threads, and data. This action cannot
              be undone.
            </span>
            <span className="block">Are you sure you want to proceed?</span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting} onClick={handleClose}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isDeleting}
            onClick={(e) => {
              e.preventDefault()
              void handleDelete()
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Deleting...' : 'Delete Workspace'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
